import React from 'react';
import jwtDecode from 'jwt-decode';
import EditorActions from '../actions/editor';
import UserSource from '../sources/user';
import moment from 'moment';

import { Alert, Modal } from 'react-bootstrap';
import { fetchCurrentTenant } from '../actions/reduxActions/currentTenant';
import { setCurrentUser } from '../actions/reduxActions/currentUser';
import { addNotification, removeAllNotifications } from '../actions/reduxActions/notification';
import { setSharingToken, setImportToken } from '../actions/reduxActions/flows';
import { stringContains } from '../utils/core';
import { connect } from 'react-redux';
import { EMAILS } from '../constants';
import { pathOr } from 'ramda';
import { navigate } from '@reach/router';

import '../../css/tenant-select.less';

export const matchesSearchQuery = ({ tenant, query }) => {

    const organizationName = pathOr(null, ['organization', 'name'], tenant);
    const searchValues = [
        tenant.developerName,
        tenant.developerSummary,
        tenant.id,
        organizationName,
    ];

    return searchValues.some(val => stringContains(val, query, false));
};

export class TenantSelect extends React.Component {

    state = {
        search: '',
        tenants: [],
        isLoading: true,
    };

    async componentDidMount() {
        try {
            const user = await UserSource.get(this.props.authenticationToken);
            const currentTenantId = this.props.tenantId;
            const otherTenants = user.tenants.filter(tenant => tenant.id !== currentTenantId);

            // Cache the user in the app state
            this.props.setCurrentUser(user);

            this.setState({
                tenants: otherTenants,
                isLoading: false });
        } catch (error) {
            this.props.addNotification({
                type: 'error',
                message: error.message,
                isPersistent: true,
            });
        }
    }

    onClick = (tenant) => {
        EditorActions.closeTenantSelector();
        navigate(`/${tenant.id}/flows`);
    }

    onSearchChange = (e) => {
        this.setState({ search: e.target.value });
    }

    render() {

        const userClaims = jwtDecode(this.props.authenticationToken);

        const tenants = this.state.tenants
            .filter(tenant => matchesSearchQuery({ tenant, query: this.state.search }))
            .map((tenant, index) => {
                const lastLoggedInDate = moment(tenant.lastLoggedInAt);
                const expiresAtDate = moment(tenant.expiresAt);

                const lastLoggedInAt = lastLoggedInDate.isValid() ? lastLoggedInDate.format('lll') : 'Unknown';
                const expiresAt = expiresAtDate.isValid() ? expiresAtDate.format('lll') : null;

                let className = 'list-group-item';
                if (index === 0 && this.props.isCloseVisible) {
                    className += ' active';
                }

                return (
                    <button type="button" className={className} onClick={this.onClick.bind(null, tenant)} key={tenant.id} disabled={tenant.isExpired}>
                        <h4 className="list-group-item-heading">
                            {`${tenant.developerName} `}
                        </h4>
                        <div className="tenant-id">{tenant.id}</div>
                        {
                            tenant.organization
                                ? <p className="list-group-item-text">{tenant.organization.name}</p>
                                : null

                        }
                        <p className="list-group-item-text">{tenant.developerSummary}</p>
                        {
                            expiresAt !== null
                                ? (
                                    <p className="list-group-item-text margin-top-small tenant-last-login">
                                        {
                                            tenant.isExpired ? `Your trial expired on ${expiresAt} and has been deactivated.` : `Tenant Expires: ${expiresAt}`
                                        }
                                    </p>
                                )
                                : null
                        }
                        <p className="list-group-item-text tenant-last-login">{`Last Login: ${lastLoggedInAt}`}</p>
                    </button>
                );
            });

        let tenantList;
        if (tenants.length) {
            tenantList = (
                <div className="list-group">
                    { tenants }
                </div>
            );

        } else if (!tenants.length && this.state.search) {
            tenantList = (
                <Alert className="search-empty" bsStyle="warning">
                    Sorry, it doesn’t look like your search matches any tenants. Please adjust your search.
                    If you are having trouble please contact your administrator.
                </Alert>
            );

        } else {
            tenantList = (
                <Alert className="tenants-empty" bsStyle="warning">
                    <strong>Oops!</strong>
                    {'\n'}
                    You don't seem to be a builder in any tenants. If you feel this is in error,
                    please contact your administrator,or the Flow support team with your user ID
                    {'\n'}
                    <b>{ userClaims.sub }</b>
                    {'\n'}
                    at
                    {'\n'}
                    <a href={`mailto:${EMAILS.support}`}><b>{ EMAILS.support }</b></a>
                </Alert>
            );
        }

        // Only display this SSO information box if we're logged in using SSO credentials
        let ssoAlert;
        if (userClaims && userClaims.sso_tenant) {
            ssoAlert = (
                <Alert>
                    <strong>Note:</strong>
                    {'\n'}
                    You're currently logged in via SSO, so you're only able to log into the
                    tenant your SSO is linked to, and any of its subtenants.
                </Alert>
            );
        }

        const content = this.state.isLoading ?
            React.createElement(manywho.component.getByName('wait'), { isVisible: true }, null) :
            (
                <div>
                    <input className="form-control" value={this.state.search} onChange={this.onSearchChange} placeholder="Search" />

                    { ssoAlert }

                    <div className="overflow-y-auto">
                        {tenantList}
                    </div>
                </div>
            );

        const footer = this.props.isCloseVisible ?
            (
                <Modal.Footer>
                    <button className="btn btn-primary" onClick={EditorActions.closeTenantSelector}>Close</button>
                </Modal.Footer>
            ) :
            null;

        return (
            <Modal show onHide={EditorActions.closeTenantSelector} container={this.props.container} dialogClassName="tenant-select">
                <Modal.Header>
                    <Modal.Title>Select a Tenant</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {content}
                </Modal.Body>
                {footer}
            </Modal>
        );
    }
}

const mapStateToProps = ({ currentUser: { authenticationToken, tenantId } }) => ({ authenticationToken, tenantId });

const mapDispatchToProps = {
    fetchCurrentTenant,
    addNotification,
    removeAllNotifications,
    setSharingToken,
    setImportToken,
    setCurrentUser,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(TenantSelect);
